import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  isSameDay,
} from "date-fns";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";
import "react-datepicker/dist/react-datepicker.css";

interface ReportDateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
}

type PresetDuration =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "custom";

export const ReportDateRangePicker: React.FC<ReportDateRangePickerProps> = ({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onChange,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [activePreset, setActivePreset] = useState<PresetDuration>("custom");
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<DatePicker>(null);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setActivePreset("custom");
  };

  const applyPreset = (preset: PresetDuration) => {
    const today = new Date();
    let start: Date | null = null;
    let end: Date | null = null;

    switch (preset) {
      case "today":
        start = today;
        end = today;
        break;
      case "yesterday":
        const yest = subDays(today, 1);
        start = yest;
        end = yest;
        break;
      case "last7":
        start = subDays(today, 6);
        end = today;
        break;
      case "last30":
        start = subDays(today, 29);
        end = today;
        break;
      case "thisMonth":
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case "lastMonth":
        const lastM = subMonths(today, 1);
        start = startOfMonth(lastM);
        end = endOfMonth(lastM);
        break;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setActivePreset(preset);
  };

  const handleApply = () => {
    onChange(startDate, endDate);
    setIsOpen(false);
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(false);
    }
  };

  const handleCancel = () => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setIsOpen(false);
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(false);
    }
  };

  const presets: { label: string; value: PresetDuration }[] = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last7" },
    { label: "Last 30 Days", value: "last30" },
    { label: "This Month", value: "thisMonth" },
    { label: "Last Month", value: "lastMonth" },
    { label: "Custom", value: "custom" },
  ];

  const formattedValue = () => {
    if (!initialStartDate) return "Select dates";
    if (initialStartDate && !initialEndDate)
      return format(initialStartDate, "MMM d, yyyy");
    return `${format(initialStartDate, "MMM d")} - ${format(
      initialEndDate!,
      "MMM d"
    )}`;
  };

  const rangeDisplay = () => {
    if (!startDate) return "";
    if (startDate && !endDate) return format(startDate, "MM/dd/yyyy");
    return `${format(startDate, "MM/dd/yyyy")} - ${format(
      endDate!,
      "MM/dd/yyyy"
    )}`;
  };

  const CalendarContainer = ({
    className,
    children,
  }: {
    className: string;
    children: React.ReactNode;
  }) => {
    return (
      <div className="flex bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden font-sans text-xs relative z-[99999]">
        {/* Sidebar */}
        <div className="w-24 flex flex-col border-r border-gray-100 py-2 bg-white flex-shrink-0">
          {presets.map((preset) => (
            <button
              key={preset.value}
              onClick={() => applyPreset(preset.value)}
              className={cn(
                "px-3 py-1.5 text-left text-[11px] font-medium transition-colors hover:bg-purple-50 hover:text-[#885ABB]",
                activePreset === preset.value
                  ? "bg-[#885ABB] text-white hover:bg-[#7748A4] hover:text-white"
                  : "text-gray-600"
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Calendar Area */}
        <div className="flex flex-col">
          <style>
            {`
                 .report-datepicker-popper {
                    z-index: 99999 !important;
                 }
                 .report-datepicker-popper .react-datepicker__triangle {
                    display: none !important;
                 }
                 .report-datepicker .react-datepicker {
                    border: none !important;
                    box-shadow: none !important;
                    display: flex !important;
                    flex-direction: row !important;
                    padding: 0 !important;
                    font-family: inherit !important;
                 }
                 .report-datepicker .react-datepicker__month-container {
                     float: none !important;
                     display: inline-block;
                     width: 14rem; /* Compact width */
                 }
                 .report-datepicker .react-datepicker__header {
                     background: white;
                     border-bottom: none;
                     padding-top: 6px;
                     padding-bottom: 2px;
                 }
                 .report-datepicker .react-datepicker__current-month {
                     color: #1f2937;
                     font-size: 0.8rem;
                     margin-bottom: 2px;
                     font-weight: 600;
                 }
                 .report-datepicker .react-datepicker__day-name {
                     color: #9ca3af;
                     width: 1.6rem;
                     line-height: 1.6rem;
                     margin: 0;
                     font-size: 0.7rem;
                     font-weight: 500;
                 }
                 .report-datepicker .react-datepicker__day {
                    width: 1.6rem;
                    line-height: 1.6rem;
                    margin: 0;
                    font-size: 0.75rem;
                 }
                 .report-datepicker .react-datepicker__navigation {
                    top: 6px;
                 }
                 .report-datepicker .react-datepicker__navigation-icon::before {
                     border-color: #4b5563;
                     border-width: 2px 2px 0 0;
                     height: 5px;
                     width: 5px;
                     top: 9px;
                 }
                 .report-datepicker .react-datepicker__day--selected,
                 .report-datepicker .react-datepicker__day--in-range,
                 .report-datepicker .react-datepicker__day--in-selecting-range {
                     background-color: #F3E8FF;
                     color: #885ABB;
                 }
                 .report-datepicker .react-datepicker__day--range-start,
                 .report-datepicker .react-datepicker__day--range-end {
                     background-color: #885ABB !important;
                     color: white !important;
                     border-radius: 4px;
                 }
                 .report-datepicker .react-datepicker__day:hover {
                     background-color: #f3f4f6;
                     border-radius: 4px;
                 }
               `}
          </style>
          <div className="p-0 report-datepicker flex justify-center">
            {children}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 p-2 flex items-center justify-between bg-gray-50/30">
            <span className="text-[11px] font-medium text-gray-500 ml-2">
              {rangeDisplay()}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="bg-white border-gray-300 hover:bg-gray-50 h-6 text-[11px] px-3"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white h-6 px-4 text-[11px]"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PortalContainer = ({ children }: { children?: React.ReactNode }) => {
    return typeof document !== "undefined" && children
      ? createPortal(children, document.body)
      : null;
  };

  return (
    <DatePicker
      ref={datePickerRef}
      selected={startDate}
      onChange={handleDateChange}
      startDate={startDate}
      endDate={endDate}
      selectsRange
      monthsShown={2}
      shouldCloseOnSelect={false}
      onCalendarOpen={() => setIsOpen(true)}
      onCalendarClose={() => setIsOpen(false)}
      calendarContainer={CalendarContainer}
      popperContainer={PortalContainer}
      popperPlacement="bottom-end"
      popperClassName="report-datepicker-popper"
      customInput={
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-600 shadow-sm cursor-pointer hover:border-purple-300 transition-colors w-fit">
          <CalendarIcon size={16} className="text-[#885ABB]" />
          <span>{formattedValue()}</span>
          <ChevronDown size={14} className="opacity-50" />
        </div>
      }
    />
  );
};
